import { SupportedLanguage, isValidLanguage } from './language-service';
import { systemLogger } from '@/lib/logging';

// Constants
const TRANSLATION_PREFERENCE_KEY = 'translation-preference';
const TRANSLATION_COOKIE_NAME = 'translationChoice';
const PREFERENCE_EXPIRY_DAYS = 30;

// Types
export interface TranslationPreference {
  choice: 'use_english' | 'allow_browser' | SupportedLanguage;
  timestamp: number;
  browserLanguage: string; // The language that triggered this choice
  version: number; // For future migrations
}

export type TranslationChoice = TranslationPreference['choice'];

/**
 * Chat translation preferences - which languages should NOT be translated
 */
export interface ChatTranslationPreference {
  disabledLanguages: SupportedLanguage[]; // Languages user doesn't want translated
  timestamp: number;
  version: number;
}

// Constants for chat translation
const CHAT_TRANSLATION_KEY = 'chat-translation-preference';
const CHAT_TRANSLATION_EXPIRY_DAYS = 90; // Longer expiry for chat preferences

/**
 * Get translation preference from localStorage
 */
export function getTranslationPreferenceFromStorage(): TranslationPreference | null {
  try {
    const stored = localStorage.getItem(TRANSLATION_PREFERENCE_KEY);
    if (!stored) return null;

    const preference: TranslationPreference = JSON.parse(stored);

    // Check if preference is expired
    const now = Date.now();
    const expiryTime = preference.timestamp + (PREFERENCE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

    if (now > expiryTime) {
      // Preference expired, remove it
      localStorage.removeItem(TRANSLATION_PREFERENCE_KEY);
      return null;
    }

    // Validate the preference structure
    if (!preference.choice || !preference.timestamp || !preference.browserLanguage) {
      localStorage.removeItem(TRANSLATION_PREFERENCE_KEY);
      return null;
    }

    return preference;
  } catch (_error) {
    systemLogger.warn('Warning:', _error);
    // Invalid JSON or other error, clean up
    localStorage.removeItem(TRANSLATION_PREFERENCE_KEY);
    return null;
  }
}

/**
 * Get translation preference from cookie (fallback)
 */
export function getTranslationPreferenceFromCookie(): TranslationPreference | null {
  try {
    const cookies = document.cookie.split(';');
    const preferenceCookie = cookies.find(cookie =>
      cookie.trim().startsWith(`${TRANSLATION_COOKIE_NAME}=`)
    );

    if (!preferenceCookie) return null;

    const cookieValue = preferenceCookie.split('=')[1]?.trim();
    if (!cookieValue) return null;
    const preference: TranslationPreference = JSON.parse(decodeURIComponent(cookieValue));

    // Check expiry
    const now = Date.now();
    const expiryTime = preference.timestamp + (PREFERENCE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

    if (now > expiryTime) {
      // Expired, remove cookie
      document.cookie = `${TRANSLATION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      return null;
    }

    return preference;
  } catch (_error) {
    systemLogger.warn('Warning:', _error);
    // Invalid cookie, clean up
    document.cookie = `${TRANSLATION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    return null;
  }
}

/**
 * Get current translation preference from any storage
 */
export function getTranslationPreference(): TranslationPreference | null {
  // Try localStorage first, then cookie
  return getTranslationPreferenceFromStorage() || getTranslationPreferenceFromCookie();
}

/**
 * Save translation preference to both localStorage and cookie
 */
export function saveTranslationPreference(
  choice: TranslationChoice,
  browserLanguage: string
): void {
  try {
    const preference: TranslationPreference = {
      choice,
      timestamp: Date.now(),
      browserLanguage,
      version: 1
    };

    // Save to localStorage
    localStorage.setItem(TRANSLATION_PREFERENCE_KEY, JSON.stringify(preference));

    // Save to cookie as backup
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + PREFERENCE_EXPIRY_DAYS);

    const cookieValue = encodeURIComponent(JSON.stringify(preference));
    document.cookie = `${TRANSLATION_COOKIE_NAME}=${cookieValue}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;

    // Successfully saved translation preference
  } catch (_error) {
    systemLogger.error('Failed operation:', _error);
  }
}

/**
 * Clear translation preference from all storage
 */
export function clearTranslationPreference(): void {
  try {
    // Remove from localStorage
    localStorage.removeItem(TRANSLATION_PREFERENCE_KEY);

    // Remove from cookie
    document.cookie = `${TRANSLATION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

    // Translation preference cleared
  } catch (_error) {
    systemLogger.error('Failed operation:', _error);
  }
}

/**
 * Check if user should see the translation choice banner
 */
export function shouldShowTranslationBanner(browserLanguage: string): boolean {
  // Don't show if browser language is already supported
  if (isValidLanguage(browserLanguage)) {
    return false;
  }

  // Don't show if user has already made a choice
  const existingPreference = getTranslationPreference();
  if (existingPreference) {
    // Check if the choice was for the same browser language
    // If user switched browser language, show banner again
    return existingPreference.browserLanguage !== browserLanguage;
  }

  // Show banner for new users with unsupported languages
  return true;
}

/**
 * Get user's preferred choice for current browser language
 */
export function getCurrentLanguageChoice(browserLanguage: string): TranslationChoice | null {
  const preference = getTranslationPreference();

  // Only return the choice if it's for the current browser language
  if (preference && preference.browserLanguage === browserLanguage) {
    return preference.choice;
  }

  return null;
}

/**
 * Check if user has allowed browser translation for current language
 */
export function isTranslationAllowedForCurrentLanguage(browserLanguage: string): boolean {
  const choice = getCurrentLanguageChoice(browserLanguage);
  return choice === 'allow_browser';
}

/**
 * Get display text for a translation choice
 */
export function getChoiceDisplayText(choice: TranslationChoice): string {
  switch (choice) {
    case 'allow_browser':
      return 'Allow Browser Translation';
    case 'use_english':
      return 'Use English';
    case 'es':
      return 'Use Spanish';
    case 'fr':
      return 'Use French';
    case 'sv':
      return 'Use Swedish';
    default:
      return 'Unknown Choice';
  }
}

/**
 * Clean up expired preferences (maintenance function)
 */
export function cleanupExpiredPreferences(): void {
  try {
    // This will automatically remove expired preferences
    getTranslationPreference();
  } catch (_error) {
    systemLogger.warn('Warning:', _error);
  }
}

/**
 * Get preference statistics for debugging
 */
export function getPreferenceDebugInfo(): {
  hasLocalStorage: boolean;
  hasCookie: boolean;
  isExpired: boolean;
  preference: TranslationPreference | null;
} {
  const localStoragePreference = getTranslationPreferenceFromStorage();
  const cookiePreference = getTranslationPreferenceFromCookie();
  const currentPreference = getTranslationPreference();

  const now = Date.now();
  const isExpired = currentPreference ?
    now > (currentPreference.timestamp + (PREFERENCE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)) :
    false;

  return {
    hasLocalStorage: !!localStoragePreference,
    hasCookie: !!cookiePreference,
    isExpired,
    preference: currentPreference
  };
}

// ============================================================
// CHAT TRANSLATION PREFERENCES (AI-driven message translation)
// ============================================================

/**
 * Get chat translation preferences from localStorage
 */
export function getChatTranslationPreference(): ChatTranslationPreference | null {
  try {
    const stored = localStorage.getItem(CHAT_TRANSLATION_KEY);
    if (!stored) return null;

    const preference: ChatTranslationPreference = JSON.parse(stored);

    // Check if preference is expired
    const now = Date.now();
    const expiryTime = preference.timestamp + (CHAT_TRANSLATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

    if (now > expiryTime) {
      localStorage.removeItem(CHAT_TRANSLATION_KEY);
      return null;
    }

    // Validate structure
    if (!preference.disabledLanguages || !Array.isArray(preference.disabledLanguages)) {
      localStorage.removeItem(CHAT_TRANSLATION_KEY);
      return null;
    }

    return preference;
  } catch (_error) {
    systemLogger.warn('Failed to parse chat translation preference', _error);
    localStorage.removeItem(CHAT_TRANSLATION_KEY);
    return null;
  }
}

/**
 * Check if chat translation is disabled for a specific language
 */
export function isChatTranslationDisabled(language: SupportedLanguage): boolean {
  const preference = getChatTranslationPreference();
  if (!preference) return false;

  return preference.disabledLanguages.includes(language);
}

/**
 * Disable chat translation for a specific language
 */
export function disableChatTranslationForLanguage(language: SupportedLanguage): void {
  try {
    const existing = getChatTranslationPreference();

    const preference: ChatTranslationPreference = {
      disabledLanguages: existing
        ? [...new Set([...existing.disabledLanguages, language])] // Add and deduplicate
        : [language],
      timestamp: Date.now(),
      version: 1
    };

    localStorage.setItem(CHAT_TRANSLATION_KEY, JSON.stringify(preference));
    systemLogger.debug('Chat translation disabled for language', { language });
  } catch (_error) {
    systemLogger.error('Failed to save chat translation preference', _error);
  }
}

/**
 * Enable chat translation for a specific language (remove from disabled list)
 */
export function enableChatTranslationForLanguage(language: SupportedLanguage): void {
  try {
    const existing = getChatTranslationPreference();
    if (!existing) return; // Nothing to enable

    const preference: ChatTranslationPreference = {
      disabledLanguages: existing.disabledLanguages.filter(lang => lang !== language),
      timestamp: Date.now(),
      version: 1
    };

    // If no disabled languages left, clear the preference
    if (preference.disabledLanguages.length === 0) {
      localStorage.removeItem(CHAT_TRANSLATION_KEY);
      systemLogger.debug('Chat translation enabled for all languages');
    } else {
      localStorage.setItem(CHAT_TRANSLATION_KEY, JSON.stringify(preference));
      systemLogger.debug('Chat translation enabled for language', { language });
    }
  } catch (_error) {
    systemLogger.error('Failed to update chat translation preference', _error);
  }
}

/**
 * Clear all chat translation preferences
 */
export function clearChatTranslationPreferences(): void {
  try {
    localStorage.removeItem(CHAT_TRANSLATION_KEY);
    systemLogger.debug('Chat translation preferences cleared');
  } catch (_error) {
    systemLogger.error('Failed to clear chat translation preferences', _error);
  }
}

/**
 * Get list of disabled languages for debugging
 */
export function getDisabledChatLanguages(): SupportedLanguage[] {
  const preference = getChatTranslationPreference();
  return preference?.disabledLanguages || [];
}